import {
	ForecastMethod,
	ForecastResult,
	RegressionResult,
} from '../../../maas/models/maas-calculations.interface';

/**
 * Ordinary least squares linear regression.
 * Points are indexed 0..n-1 on the x-axis.
 */
export function linearRegression(values: number[]): RegressionResult {
	const n = values.length;
	if (n < 2) return { slope: 0, intercept: values[0] ?? 0, r2: 0 };
	let sumX = 0;
	let sumY = 0;
	let sumXY = 0;
	let sumX2 = 0;
	for (let i = 0; i < n; i++) {
		sumX += i;
		sumY += values[i];
		sumXY += i * values[i];
		sumX2 += i * i;
	}
	const meanY = sumY / n;
	const denom = n * sumX2 - sumX * sumX;
	if (denom === 0) return { slope: 0, intercept: meanY, r2: 0 };
	const slope = (n * sumXY - sumX * sumY) / denom;
	const intercept = (sumY - slope * sumX) / n;
	// R-squared
	let ssRes = 0;
	let ssTot = 0;
	for (let i = 0; i < n; i++) {
		const predicted = intercept + slope * i;
		ssRes += (values[i] - predicted) ** 2;
		ssTot += (values[i] - meanY) ** 2;
	}
	const r2 = ssTot === 0 ? 0 : 1 - ssRes / ssTot;
	return { slope, intercept, r2 };
}

/**
 * Simple Exponential Smoothing (SES).
 * Auto-tunes alpha by minimizing MAE if not provided.
 */
export function simpleExponentialSmoothing(
	data: number[],
	alpha?: number
): { fitted: number[]; fittedAlpha: number } {
	if (data.length === 0) return { fitted: [], fittedAlpha: 0 };
	if (data.length === 1) return { fitted: [data[0]], fittedAlpha: 0.5 };
	const bestAlpha = alpha ?? optimizeAlpha(data);
	const fitted: number[] = [data[0]];
	for (let i = 1; i < data.length; i++) {
		fitted.push(bestAlpha * data[i] + (1 - bestAlpha) * fitted[i - 1]);
	}
	return { fitted, fittedAlpha: bestAlpha };
}

function optimizeAlpha(data: number[]): number {
	let bestAlpha = 0.3;
	let bestMae = Infinity;
	for (let a = 0.05; a <= 0.95; a += 0.05) {
		let level = data[0];
		let mae = 0;
		for (let i = 1; i < data.length; i++) {
			mae += Math.abs(data[i] - level);
			level = a * data[i] + (1 - a) * level;
		}
		mae /= data.length - 1;
		if (mae < bestMae) {
			bestMae = mae;
			bestAlpha = a;
		}
	}
	return Math.round(bestAlpha * 100) / 100;
}

/**
 * Holt's double exponential smoothing (level + trend).
 * Auto-tunes alpha and beta if not provided.
 */
export function holtForecast(
	data: number[],
	horizon: number,
	alpha?: number,
	beta?: number
): ForecastResult {
	if (data.length < 2) {
		const val = data[0] ?? 0;
		return {
			method: 'holt',
			predicted: Array(horizon).fill(val),
			lower: Array(horizon).fill(val),
			upper: Array(horizon).fill(val),
			fitted: data.length ? [val] : [],
			residuals: data.length ? [0] : [],
		};
	}

	const { bestAlpha, bestBeta } =
		alpha !== undefined && beta !== undefined
			? { bestAlpha: alpha, bestBeta: beta }
			: optimizeHoltParams(data);

	// Initialize
	let level = data[0];
	let trend = data[1] - data[0];

	const fitted: number[] = [level];
	const residuals: number[] = [data[0] - level];

	// Fit
	for (let i = 1; i < data.length; i++) {
		const prevLevel = level;
		const prevTrend = trend;
		level = bestAlpha * data[i] + (1 - bestAlpha) * (prevLevel + prevTrend);
		trend = bestBeta * (level - prevLevel) + (1 - bestBeta) * prevTrend;
		fitted.push(prevLevel + prevTrend);
		residuals.push(data[i] - fitted[i]);
	}

	// Forecast
	const predicted: number[] = [];
	for (let h = 1; h <= horizon; h++) {
		predicted.push(level + h * trend);
	}

	// Confidence intervals from residuals
	const { lower, upper } = computeConfidenceInterval(residuals, predicted);

	return { method: 'holt', predicted, lower, upper, fitted, residuals };
}

function optimizeHoltParams(data: number[]): { bestAlpha: number; bestBeta: number } {
	let bestAlpha = 0.3;
	let bestBeta = 0.1;
	let bestMae = Infinity;

	for (let a = 0.1; a <= 0.9; a += 0.1) {
		for (let b = 0.05; b <= 0.5; b += 0.05) {
			let level = data[0];
			let trend = data[1] - data[0];
			let mae = 0;

			for (let i = 1; i < data.length; i++) {
				const forecastVal = level + trend;
				mae += Math.abs(data[i] - forecastVal);
				const prevLevel = level;
				level = a * data[i] + (1 - a) * (prevLevel + trend);
				trend = b * (level - prevLevel) + (1 - b) * trend;
			}

			mae /= data.length - 1;
			if (mae < bestMae) {
				bestMae = mae;
				bestAlpha = a;
				bestBeta = b;
			}
		}
	}

	return {
		bestAlpha: Math.round(bestAlpha * 100) / 100,
		bestBeta: Math.round(bestBeta * 100) / 100,
	};
}

/**
 * Linear forecast: extends the regression line into the future.
 */
export function linearForecast(data: number[], horizon: number): ForecastResult {
	const reg = linearRegression(data);
	const n = data.length;

	const fitted = data.map((_, i) => reg.intercept + reg.slope * i);
	const residuals = data.map((v, i) => v - fitted[i]);

	const predicted: number[] = [];
	for (let h = 1; h <= horizon; h++) {
		predicted.push(reg.intercept + reg.slope * (n - 1 + h));
	}

	const { lower, upper } = computeConfidenceInterval(residuals, predicted);

	return { method: 'linear', predicted, lower, upper, fitted, residuals };
}

/**
 * SES forecast: flat projection from last smoothed value.
 */
export function sesForecast(data: number[], horizon: number): ForecastResult {
	const { fitted, fittedAlpha: _ } = simpleExponentialSmoothing(data);
	const lastSmoothed = fitted[fitted.length - 1];
	const residuals = data.map((v, i) => v - fitted[i]);

	const predicted = Array(horizon).fill(lastSmoothed);
	const { lower, upper } = computeConfidenceInterval(residuals, predicted);

	return { method: 'ses', predicted, lower, upper, fitted, residuals };
}

/**
 * Compute 80% prediction intervals from residuals.
 * Uses z=1.28 for 80% confidence.
 */
export function computeConfidenceInterval(
	residuals: number[],
	forecast: number[]
): { lower: number[]; upper: number[] } {
	const n = residuals.length;
	if (n === 0) return { lower: [...forecast], upper: [...forecast] };

	const meanResidual = residuals.reduce((s, r) => s + r, 0) / n;
	const variance = residuals.reduce((s, r) => s + (r - meanResidual) ** 2, 0) / Math.max(n - 1, 1);
	const stdDev = Math.sqrt(variance);

	const z = 1.28; // 80% confidence
	const lower = forecast.map((v, i) => v - z * stdDev * Math.sqrt(1 + (i + 1) / n));
	const upper = forecast.map((v, i) => v + z * stdDev * Math.sqrt(1 + (i + 1) / n));

	return { lower, upper };
}

/**
 * Auto-select the best forecasting method based on data characteristics.
 * - If strong linear trend (R2 > 0.7): linear
 * - If moderate trend (R2 > 0.3): holt (captures trend with smoothing)
 * - Otherwise: ses (stationary data)
 */
export function autoSelectMethod(data: number[]): ForecastMethod {
	if (data.length < 3) return 'ses';

	const reg = linearRegression(data);

	if (reg.r2 > 0.7) return 'linear';
	if (reg.r2 > 0.3) return 'holt';
	return 'ses';
}

/**
 * Run the full forecast pipeline: auto-select method, compute forecast.
 */
export function forecast(
	data: number[],
	horizon: number,
	method: ForecastMethod | 'auto' = 'auto'
): ForecastResult {
	const selectedMethod = method === 'auto' ? autoSelectMethod(data) : method;

	switch (selectedMethod) {
		case 'linear':
			return linearForecast(data, horizon);
		case 'holt':
			return holtForecast(data, horizon);
		case 'ses':
			return sesForecast(data, horizon);
		case 'seasonal':
			// Seasonal requires day-of-week info — fallback to SES if called
			// without it (should not happen; tool layer handles seasonal path)
			return sesForecast(data, horizon);
	}
}
